import { ConfirmInfoStep } from "@/components/mentor-profile/confirm-info-step";
import { PostApprovalHome } from "@/components/mentor-profile/post-approval-home";
import { RevisionFeedbackPopup } from "@/components/mentor-profile/revision-feedback-popup";
import { ReviewStep } from "@/components/mentor-profile/review-step";
import { ThanksSubmitted } from "@/components/mentor-profile/thanks-submitted";
import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import { WizardIntro } from "@/components/mentor-profile/wizard-intro";
import {
  WIZARD_STEP_LIST,
  type WizardStepComponent,
} from "@/components/mentor-profile/wizard-step-registry";
import type { MentorProfileReview } from "@/lib/api/mentor-profile";
import { useMentorProfileReview } from "@/lib/api/mentor-profile";
import {
  useMentorProfileDraft,
  type UseMentorProfileDraftResult,
} from "@/lib/hooks/mentor-profile/use-mentor-profile-draft";
import { buildStepContinueEvent } from "@/lib/mentor-profile/analytics";
import {
  flaggedBuildIndices,
  firstFlaggedBuildIndex,
  notesForStep,
  resolveOnboardingEntry,
} from "@/lib/mentor-profile/resolve-onboarding-entry";
import {
  clearRevisionBaseline,
  readOrWriteRevisionBaseline,
} from "@/lib/mentor-profile/revision-baseline";
import { openChangeRequests } from "@/lib/mentor-profile/submit-readiness";
import {
  WIZARD_BUILD_STEPS,
  type OnboardingPhase,
  type WizardFurthestStep,
} from "@/lib/mentor-profile/wizard-steps";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

function BuildStepsFlow({
  draftState,
  stepIndex,
  onStepIndexChange,
  onExit,
  onReview,
  reviewerNotes,
  mentorId,
  stepPath,
}: {
  draftState: UseMentorProfileDraftResult;
  stepIndex: number;
  onStepIndexChange: (index: number) => void;
  onExit: () => void;
  onReview: () => void;
  reviewerNotes: string[];
  mentorId: string;
  stepPath: number[];
}) {
  const { track } = useTrackEvent();
  const { draft, saveState, setField, setFields, flush, markFurthestStep } =
    draftState;

  const pathPos = stepPath.indexOf(stepIndex);

  const goTo = (index: number) => {
    flush();
    const step = WIZARD_BUILD_STEPS[index];
    if (step) markFurthestStep(step);
    onStepIndexChange(index);
  };

  const CurrentStep = WIZARD_STEP_LIST[stepIndex]?.Component as
    | WizardStepComponent
    | undefined;
  if (!CurrentStep || pathPos < 0) return null;

  const isLastStep = pathPos === stepPath.length - 1;
  const stepKey = WIZARD_BUILD_STEPS[stepIndex];

  return (
    <CurrentStep
      draft={draft}
      saveState={saveState}
      setField={setField}
      setFields={setFields}
      reviewerNotes={reviewerNotes}
      onBack={() => {
        if (pathPos > 0) {
          goTo(stepPath[pathPos - 1]!);
          return;
        }
        onExit();
      }}
      onContinue={() => {
        if (stepKey) {
          const mapped = buildStepContinueEvent(stepKey, draft, mentorId);
          if (mapped) track(mapped.event, mapped.props);
        }
        if (isLastStep) {
          flush();
          markFurthestStep("review");
          onReview();
          return;
        }
        goTo(stepPath[pathPos + 1]!);
      }}
    />
  );
}

function OnboardingFlow({
  initialReview,
}: {
  initialReview: MentorProfileReview;
}) {
  const { track } = useTrackEvent();
  // Freeze entry at mount so autosave cache updates don't drift baselineDraft
  // (needed for revision change detection / resubmit gate). Persist the
  // baseline in sessionStorage so refresh mid-revision keeps the pre-edit copy.
  const [entry] = useState(() => {
    const resolved = resolveOnboardingEntry(initialReview);
    const roundKey = resolved.openRequests[0]?.created_at;
    if (!roundKey || !resolved.baselineDraft) return resolved;
    return {
      ...resolved,
      baselineDraft: readOrWriteRevisionBaseline(
        initialReview.id,
        roundKey,
        resolved.baselineDraft,
      ),
    };
  });
  const draftState = useMentorProfileDraft(
    initialReview.draft,
    initialReview.furthest_step,
  );
  const [phase, setPhase] = useState<OnboardingPhase>(entry.phase);
  const [buildStepIndex, setBuildStepIndex] = useState(entry.buildStepIndex);
  const { baselineDraft, openRequests } = entry;
  const revisionRoundKey = openRequests[0]?.created_at ?? "none";
  const mentorId = initialReview.mentor_id;

  const isRevision = openRequests.length > 0;
  const stepPath = useMemo(
    () =>
      isRevision
        ? flaggedBuildIndices(openRequests)
        : WIZARD_BUILD_STEPS.map((_, index) => index),
    [isRevision, openRequests],
  );

  const currentStepKey = WIZARD_BUILD_STEPS[buildStepIndex];
  const reviewerNotes = currentStepKey
    ? notesForStep(openRequests, currentStepKey)
    : [];

  const enterPhase = (next: OnboardingPhase, furthest?: WizardFurthestStep) => {
    if (furthest) draftState.markFurthestStep(furthest);
    setPhase(next);
  };

  switch (phase) {
    case "approved":
      return (
        <PostApprovalHome
          draft={draftState.draft}
          mentorId={mentorId}
          reviewCreatedAt={initialReview.created_at}
          changeRequests={initialReview.change_requests}
        />
      );
    case "submitted":
      return <ThanksSubmitted draft={draftState.draft} />;
    case "revision_feedback":
      return (
        <RevisionFeedbackPopup
          requests={openRequests}
          mentorId={mentorId}
          reviewRound={
            new Set(openRequests.map((r) => r.created_at)).size || 1
          }
          onDismiss={() => {
            setBuildStepIndex(firstFlaggedBuildIndex(openRequests));
            setPhase("build");
          }}
        />
      );
    case "confirm":
      return (
        <ConfirmInfoStep
          draft={draftState.draft}
          saveState={draftState.saveState}
          setField={draftState.setField}
          mentorId={mentorId}
          onContinue={() => {
            track(PostHogEvents.MENTOR_PROFILE_CONFIRM_INFO_COMPLETED, {
              mentor_id: mentorId,
            });
            draftState.flush();
            enterPhase("intro", "intro");
          }}
        />
      );
    case "intro":
      return (
        <WizardIntro
          onBack={() => setPhase("confirm")}
          onContinue={() => {
            draftState.markFurthestStep("profile_photo");
            setBuildStepIndex(0);
            setPhase("build");
          }}
        />
      );
    case "review":
      return (
        <ReviewStep
          draft={draftState.draft}
          saveState={draftState.saveState}
          changeRequests={openRequests}
          baselineDraft={baselineDraft ?? undefined}
          reviewUpdatedAt={initialReview.updated_at}
          mentorId={mentorId}
          onKeepEditing={() => {
            setBuildStepIndex(
              openRequests.length > 0
                ? firstFlaggedBuildIndex(openRequests)
                : WIZARD_STEP_LIST.length - 1,
            );
            setPhase("build");
          }}
          onSubmitted={() => {
            clearRevisionBaseline(initialReview.id, revisionRoundKey);
            setPhase("submitted");
          }}
        />
      );
    case "build":
      return (
        <BuildStepsFlow
          draftState={draftState}
          stepIndex={buildStepIndex}
          onStepIndexChange={setBuildStepIndex}
          onExit={() => setPhase(isRevision ? "review" : "intro")}
          onReview={() => setPhase("review")}
          reviewerNotes={reviewerNotes}
          mentorId={mentorId}
          stepPath={stepPath}
        />
      );
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <WizardBackground>
      <div className="relative z-10 flex flex-col items-center gap-4 text-center">
        {children}
      </div>
    </WizardBackground>
  );
}

export function MentorProfileWizard() {
  const { data: review, isPending, isError } = useMentorProfileReview();

  // The design opens on a themed "verifying" state while the application data
  // is prefilled. Hold it briefly so it's visible even when the fetch resolves
  // instantly (e.g. mock mode).
  const [verifying, setVerifying] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setVerifying(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (isPending || verifying) {
    return (
      <FullScreenMessage>
        <Loader2 className="size-10 animate-spin text-[#7178ff]" />
        <p className="font-body text-[15px] text-[#797774]">
          Verifying your details...
        </p>
      </FullScreenMessage>
    );
  }

  if (isError || !review) {
    return (
      <FullScreenMessage>
        <p className="font-body text-[15px] text-[#797774]">
          Something went wrong loading your profile. Please try again.
        </p>
      </FullScreenMessage>
    );
  }

  // Don't key on status — remounting on status would re-baseline against an
  // already-edited draft once a revision round is open.
  const revisionRoundKey =
    openChangeRequests(review.change_requests)[0]?.created_at ?? "none";

  return (
    <OnboardingFlow
      key={`${review.id}-${revisionRoundKey}`}
      initialReview={review}
    />
  );
}
